import { AdjustmentBreakdown, isDefined } from '@aloft/toolkit-shared';

import { PDFOnly } from '../../summary/components/PDFOnly.tsx';
import { Tr } from '../../summary/components/Tr.tsx';
import { WebOnly } from '../../summary/components/WebOnly.tsx';
import { AdjustmentTooltip } from './AdjustmentTooltip.tsx';
import { SummaryTableHorizontalCell } from './SummaryTableHorizontalCell.tsx';
import {
  BreakdownColumn,
  Tkv2PairedCostSummaryInterfaceV2,
  isSingleColumn,
  isSubRows,
} from './types.ts';
import { cellBackgroundColor } from './utils/cellBackgroundColor.ts';
import { getPropertyFromSummary } from './utils/getPropertyFromSummary.ts';

type SummaryTableHorizontalPropertyRowsProps = {
  columns: BreakdownColumn[];
  tableDataRow?: unknown[];
  header: string;
  summary: Tkv2PairedCostSummaryInterfaceV2;
  isFirstRow?: boolean;
  compId?: string;
};

export const SummaryTableHorizontalPropertyRows = ({
  columns,
  tableDataRow = [],
  header,
  summary,
  isFirstRow = false,
  compId,
}: SummaryTableHorizontalPropertyRowsProps) => {
  const subColIdx = columns.findIndex(isSubRows);
  const rowSpan = subColIdx > -1 ? (tableDataRow[subColIdx] as Array<unknown>).length : 1;

  return [...Array(rowSpan).keys()].map((rowIdx) => {
    return (
      <Tr key={`${header}-${rowIdx}`}>
        {/* Render row header */}
        {rowIdx == 0 ? (
          <SummaryTableHorizontalCell
            isFirstRow={isFirstRow}
            isFirstColumn
            rowSpan={rowSpan}
            cellValue={header}
          />
        ) : (
          // the pdf doesn't actually know about row span. we have to
          // render empty cells to take up the space instead, which
          // isn't as nice, but it's better than a mis-aligned table.
          <PDFOnly>
            <SummaryTableHorizontalCell isFirstColumn cellValue="" />
          </PDFOnly>
        )}

        {/* Render row data columns */}
        {columns.map((col, colIdx) => {
          if (isSubRows(col)) {
            const subRowData =
              (tableDataRow as Array<Array<Array<unknown>>>)?.[colIdx]?.[rowIdx] ?? [];

            return subRowData.map((val: unknown, subColIdx: number) => {
              // subtract one for the label column in the tableData
              const subColDef = col.subRows[rowIdx]?.columns[subColIdx - 1];
              return (
                <SummaryTableHorizontalCell
                  key={`${header}-${rowIdx}-${subColIdx}}`}
                  cellValue={val}
                  valueFormatter={subColDef?.valueFormatter}
                  isFirstRow={isFirstRow}
                  cellBackgroundColor={cellBackgroundColor(subColDef, summary, 'subject')}
                  typographyVariant={subColDef?.typographyVariant}
                />
              );
            });
          }

          const key = `${header}-${col.header}`;

          if (rowIdx === 0) {
            const { includeTooltip } = col;
            const property =
              isSingleColumn(col) && isDefined(compId)
                ? getPropertyFromSummary(summary, compId, col)
                : undefined;

            return (
              <SummaryTableHorizontalCell
                key={key}
                cellValue={tableDataRow?.[colIdx]}
                valueFormatter={col.valueFormatter}
                isFirstRow={isFirstRow}
                rowSpan={rowSpan}
                cellBackgroundColor={cellBackgroundColor(col, summary, compId)}
                typographyVariant={col.typographyVariant}
              >
                {includeTooltip && (
                  <WebOnly>
                    <AdjustmentTooltip {...(property as AdjustmentBreakdown)} />
                  </WebOnly>
                )}
              </SummaryTableHorizontalCell>
            );
          } else {
            return (
              <PDFOnly key={key}>
                <SummaryTableHorizontalCell cellValue="" />
              </PDFOnly>
            );
          }
        })}
      </Tr>
    );
  });
};
